/**
 * PromptStudioPanel.jsx · v2.7.45+E16 (Phase 6 · Feb-2026)
 * ─────────────────────────────────────────────────────────────────────
 * Live-edit the four LLM system prompts used by the platform:
 *   1. translation_en_es      — bilingual chat translator
 *   2. chatbot_responder      — FAQ autoresponder
 *   3. smart_reply_suggestions— staff one-click reply chips
 *   4. ticket_auto_summarizer — chat→ticket subject/summary
 *
 * Edit history is preserved server-side (last 20 versions per prompt)
 * and can be reverted from this panel.
 */
import { useCallback, useEffect, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, History, Variable, Code2, AlertTriangle } from "lucide-react";

export default function PromptStudioPanel() {
  const [prompts, setPrompts] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/ai-prompts");
      const items = r.data?.items || [];
      setPrompts(items);
      if (!activeKey && items.length) setActiveKey(items[0].key);
    } catch (e) {
      toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [activeKey]);

  const loadDetail = useCallback(async (key) => {
    if (!key) return;
    try {
      const r = await adminApi.get(`/admin/ai-prompts/${key}`);
      setDetail(r.data);
      setDraft(r.data?.content || "");
    } catch (e) {
      toast.error(`Detail load failed: ${e?.response?.data?.detail || e.message}`);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (activeKey) loadDetail(activeKey); }, [activeKey, loadDetail]);

  const save = async () => {
    if (!detail) return;
    if (draft.trim().length < 10) { toast.error("Prompt body too short"); return; }
    setSaving(true);
    try {
      await adminApi.put(`/admin/ai-prompts/${detail.key}`, { content: draft });
      toast.success(`Saved · ${detail.label}`);
      loadList();
      loadDetail(detail.key);
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  const revert = async (version) => {
    if (!detail) return;
    if (!window.confirm(`Revert "${detail.label}" to version ${version}? This pushes the current version into history.`)) return;
    try {
      await adminApi.post(`/admin/ai-prompts/${detail.key}/revert`, { version });
      toast.success(`Reverted to v${version}`);
      loadDetail(detail.key);
      loadList();
    } catch (e) { toast.error(`Revert failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const dirty = detail && draft !== (detail.content || "");

  return (
    <div className="space-y-4" data-testid="dev-tab-prompts-body">
      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-300 dark:border-amber-900/40 rounded p-3 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2" data-testid="prompts-warning">
        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0"/>
        <div>
          <b>Live LLM prompts.</b> Edits take effect within 30 s (cache window) and apply to every conversation system-wide. Keep <code className="font-mono bg-amber-100 dark:bg-amber-900/30 px-1 rounded">{`{{variables}}`}</code> intact — they're substituted at call time. History keeps the last 20 versions per prompt.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
        {/* Sidebar */}
        <div className="border border-slate-200 dark:border-zinc-800 rounded overflow-hidden bg-white dark:bg-zinc-950" data-testid="prompts-list">
          {loading && (
            <div className="text-center py-6 text-xs text-slate-500"><Loader2 className="h-4 w-4 mx-auto animate-spin"/>Loading…</div>
          )}
          {prompts.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setActiveKey(p.key)}
              className={`w-full text-left px-3 py-2.5 border-b border-slate-100 dark:border-zinc-900 hover:bg-slate-50 dark:hover:bg-zinc-900/50
                ${activeKey === p.key ? "bg-violet-50 dark:bg-violet-900/10 border-l-4 border-l-violet-600" : ""}`}
              data-testid={`prompt-row-${p.key}`}
            >
              <div className="flex items-center gap-2">
                <Code2 className="h-3.5 w-3.5 text-violet-500 flex-shrink-0"/>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-xs truncate">{p.label}</div>
                  <div className="text-[10px] text-slate-500 truncate font-mono">{p.key}</div>
                </div>
                {!p.is_default && <span className="text-[9px] uppercase tracking-widest text-emerald-600 font-bold">v{p.version}</span>}
                {p.is_default && <span className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">DEFAULT</span>}
              </div>
            </button>
          ))}
        </div>

        {/* Editor */}
        <div className="border border-slate-200 dark:border-zinc-800 rounded bg-white dark:bg-zinc-950 p-4" data-testid="prompts-editor">
          {!detail ? (
            <div className="text-center py-12 text-sm text-slate-500 italic">Select a prompt on the left to edit.</div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 font-mono">{detail.key}</div>
                  <h3 className="font-extrabold text-base">{detail.label}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowHistory((s) => !s)} className="h-7 text-xs gap-1.5" data-testid="prompt-history-toggle">
                    <History className="h-3 w-3"/>History ({detail.history?.length || 0})
                  </Button>
                  <Button onClick={save} disabled={!dirty || saving} className="bg-violet-600 hover:bg-violet-500 text-white gap-1.5 h-7 text-xs" data-testid="prompt-save-btn">
                    {saving ? <Loader2 className="h-3 w-3 animate-spin"/> : <Save className="h-3 w-3"/>}Save
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-600 dark:text-zinc-400 mb-2">{detail.description}</p>
              {detail.variables?.length > 0 && (
                <div className="mb-2 text-[11px] flex items-center gap-2 flex-wrap" data-testid="prompt-variables">
                  <Variable className="h-3 w-3 text-violet-500"/>
                  <span className="text-slate-500 uppercase tracking-widest font-bold text-[10px]">Variables:</span>
                  {detail.variables.map((v) => (
                    <code key={v} className="font-mono bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 rounded">{`{{${v}}}`}</code>
                  ))}
                </div>
              )}
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={14}
                className="text-xs font-mono"
                data-testid="prompt-editor-textarea"
              />
              <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                <span>{draft.length} chars · v{detail.version}{detail.updated_at ? ` · updated ${detail.updated_at.slice(0, 16).replace("T", " ")} by ${detail.updated_by}` : " · never edited"}</span>
                {dirty && <span className="text-amber-600 font-bold">● unsaved changes</span>}
              </div>

              {showHistory && (
                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-zinc-800" data-testid="prompt-history-list">
                  <h4 className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Edit history</h4>
                  {(!detail.history || detail.history.length === 0) && <div className="text-xs text-slate-500 italic">No previous versions.</div>}
                  <div className="space-y-1.5">
                    {(detail.history || []).map((h) => (
                      <div key={`${h.version}-${h.at}`} className="border border-slate-200 dark:border-zinc-800 rounded p-2 text-xs" data-testid={`prompt-history-row-${h.version}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold">v{h.version}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-slate-500">{h.at?.slice(0, 16).replace("T", " ")} · {h.by}</span>
                            <Button variant="ghost" size="sm" onClick={() => revert(h.version)} className="h-6 text-[10px] gap-1" data-testid={`prompt-revert-${h.version}`}>
                              <RotateCcw className="h-3 w-3"/>Revert
                            </Button>
                          </div>
                        </div>
                        <div className="font-mono text-[10px] text-slate-500 truncate">{h.content?.slice(0, 200)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
